import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, errorText, type PlanDto, type PlanCommitResult } from "../api/client";
import { OperationCard } from "./OperationEditor";
import { Questions } from "./Questions";

/** Gemeinsame Vorschau für Text, Bilder und Sprache. Nur der versionierte Serverplan wird freigegeben. */
export function PlanDecision({ planId, onDone }: { planId: string; onDone?: () => void }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["plan", planId], queryFn: () => api<PlanDto>(`/api/v1/action-plans/${planId}`) });
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState<Record<number, Record<string, unknown>>>({});
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { setEdits({}); setSelected({}); setEditing(false); }, [query.data?.planVersion]);
  const dirty = Object.keys(edits).length > 0 || Object.keys(selected).length > 0;
  const confirm = useMutation({
    mutationFn: async () => {
      const plan = query.data!;
      setError("");
      if (dirty) {
        const updated = await api<PlanDto>(`/api/v1/action-plans/${planId}`, { method: "PATCH", body: {
          expectedPlanVersion: plan.planVersion,
          operations: plan.operations.filter(op => edits[op.index] || selected[op.index] !== undefined).map(op => ({
            index: op.index, payload: edits[op.index] ? { ...(op.payload as object), ...edits[op.index] } : null, selected: selected[op.index] ?? null,
          })),
        } });
        qc.setQueryData(["plan", planId], updated);
        setMessage(updated.approvalClass === "blocked" ? "Bitte die markierten Angaben ergänzen." : "Änderungen geprüft. Vorschlag jetzt bestätigen.");
        return false;
      }
      await api<PlanCommitResult>(`/api/v1/action-plans/${planId}/approve-and-commit`, { body: { planVersion: plan.planVersion, planHash: plan.planHash } });
      return true;
    },
    onSuccess: async committed => { await qc.invalidateQueries(); if (committed) onDone?.(); },
    onError: async e => { setError(errorText(e)); await qc.invalidateQueries({ queryKey: ["plan", planId] }); },
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/v1/action-plans/${planId}/reject`, { method: "POST", body: {} }),
    onSuccess: async () => { await qc.invalidateQueries(); onDone?.(); },
    onError: e => setError(errorText(e)),
  });
  if (query.isPending) return <p role="status">Vorschlag wird geladen …</p>;
  if (query.isError) return <p className="error">{errorText(query.error)}</p>;
  const plan = query.data;
  if (plan.status === "committed") return <p className="ok" role="status">Gespeichert.</p>;
  if (plan.status !== "awaiting_review" && plan.status !== "needs_answer") return <p className="muted">Vorschlag abgeschlossen.</p>;
  const busy = confirm.isPending || remove.isPending;
  return <section className="plan-decision" aria-label="Erkannter Vorschlag">
    <h3>So habe ich es verstanden</h3>
    {plan.summary && <p>{plan.summary}</p>}
    {plan.inputId && <Questions inputId={plan.inputId} questions={plan.questions} />}
    <fieldset className="proposal-fields" disabled={busy}><legend className="sr-only">Erkannte Angaben</legend><ol className="ops">{plan.operations.map(op => <OperationCard key={`${plan.planVersion}-${op.index}`} op={op} editable={editing}
      selected={selected[op.index] ?? op.selected} onSelect={value => setSelected(s => ({ ...s, [op.index]: value }))}
      onEdit={patch => setEdits(s => ({ ...s, [op.index]: { ...s[op.index], ...patch } }))} />)}</ol></fieldset>
    {plan.approvalClass === "blocked" && !plan.questions.some(q => !q.answer) && <p className="warn">Markierte Angaben müssen ergänzt werden. Mit „Ändern“ öffnest du die Felder.</p>}
    {message && <p role="status">{message}</p>}
    {error && <p className="error" role="alert">{error}</p>}
    <div className="row wrap">
      <button className="btn primary" disabled={busy || (plan.approvalClass === "blocked" && !dirty)} onClick={() => confirm.mutate()}>{confirm.isPending ? "Prüfe …" : dirty ? "Änderungen prüfen" : "Bestätigen"}</button>
      <button className="btn" disabled={busy} aria-pressed={editing} onClick={() => setEditing(!editing)}>Ändern</button>
      <button className="btn ghost danger-text" disabled={busy} onClick={() => remove.mutate()}>Löschen</button>
    </div>
  </section>;
}
